import { sum } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { ComponentDesign, isTruss, Member, PlatePair } from '@paragon/contract-library';
import { distanceEquals, distanceGreaterThanOrEqual } from '@paragon/geometry-library';
import { getLumberCode, getNominalBoardFeet, getPlatePairWeightInPounds } from '@paragon/material-lists';
import { calculateMemberOverallLength } from '@paragon/three-shared';
import { notNullish } from '@paragon/utilities';

import { RootState } from '..';
import { getActiveComponentDesign } from './getActiveComponentDesignSelector';
import { getComponentInventoryStockLengths } from './getComponentInventoryStockLengths';

const getPrices = (state: RootState) => state.inventoryPrices;

export function getPlatePairPrice(prices: RootState['inventoryPrices'], platePair: PlatePair): number | null {
  const pricePerPound = prices.platePricesPerPound[platePair.plateType];
  return pricePerPound == null ? null : pricePerPound * getPlatePairWeightInPounds(platePair);
}

export const createGetMemberPrice = createSelector(
  getComponentInventoryStockLengths,
  getPrices,
  (stockLengths, prices) =>
    (componentDesign: ComponentDesign, member: Member): number | null => {
      if (!stockLengths) return null;

      const stockLengthsForLumber = stockLengths.getStockLengths(componentDesign, member.lumber);
      const memberLength = calculateMemberOverallLength(member, componentDesign.component);
      if (!stockLengthsForLumber || !memberLength) return null;

      const sufficientStockLengths = stockLengthsForLumber
        .filter((length) => distanceGreaterThanOrEqual(length, memberLength))
        .toSorted((a, b) => a - b);
      if (sufficientStockLengths.length === 0) return null;

      for (const stockLength of sufficientStockLengths) {
        const stockLengthPrices = prices.lumberPrices[getLumberCode(member.lumber)];
        if (!stockLengthPrices) continue;

        const priceForOneThousandBoardFeet = stockLengthPrices.find((stockLengthPrice) =>
          distanceEquals(stockLengthPrice.stockLength, stockLength),
        )?.price;
        if (!priceForOneThousandBoardFeet) continue;

        const nominalBoardFeet = getNominalBoardFeet(member.lumber, stockLength);
        return nominalBoardFeet * (priceForOneThousandBoardFeet / 1000);
      }

      return null;
    },
);

type PriceInformation = {
  priceText: string;
  priceUnavailableReason?: string;
};

export const getPriceForActiveComponentDesign = createSelector(
  getActiveComponentDesign,
  createGetMemberPrice,
  getPrices,
  (activeComponentDesign, getMemberPrice, prices): PriceInformation => {
    const notAvailable = 'N/A';
    if (activeComponentDesign == null) return { priceText: notAvailable };

    const priceUnavailableReasons: string[] = [];

    const { component } = activeComponentDesign;
    const componentMemberNamesAndPrices = component.members.map((member) => ({
      name: member.name,
      price: getMemberPrice(activeComponentDesign, member),
    }));
    const membersWithoutPrices = componentMemberNamesAndPrices.filter(
      (memberNameAndPrice) => memberNameAndPrice.price === null,
    );
    if (membersWithoutPrices.length > 0) {
      priceUnavailableReasons.push(
        `Members without associated lumber prices: ${membersWithoutPrices
          .map((memberNameAndPrice) => memberNameAndPrice.name)
          .join(', ')}`,
      );
    }
    const summedLumberPrices = sum(componentMemberNamesAndPrices.map((member) => member.price).filter(notNullish));

    const componentPlatePrices = component.platePairs.map((platePair) => ({
      name: platePair.name,
      price: getPlatePairPrice(prices, platePair),
    }));
    const platesWithoutPrices = componentPlatePrices.filter(
      (platePairNameAndPrice) => platePairNameAndPrice.price === null,
    );
    if (platesWithoutPrices.length > 0) {
      priceUnavailableReasons.push(
        `Plates without associated prices: ${platesWithoutPrices
          .map((plateNameAndPrice) => plateNameAndPrice.name)
          .join(', ')}`,
      );
    }
    const summedPlatePrices = sum(componentPlatePrices.map((plateAndPrice) => plateAndPrice.price).filter(notNullish));

    if (priceUnavailableReasons.length > 0) {
      return {
        priceText: notAvailable,
        priceUnavailableReason: priceUnavailableReasons.join('; '),
      };
    }

    const componentPrice =
      (summedLumberPrices + summedPlatePrices) * (isTruss(component) ? component.numberOfPlies : 1);
    return { priceText: `$${componentPrice.toFixed(2)}` };
  },
);
